import cx from "classnames";
import * as React from "react";
import TooltipWrapper from "../../TooltipWrapper";
import { FormFieldStates, InputProps } from "./exports";

export type QESSelectProps = InputProps & React.SelectHTMLAttributes<HTMLSelectElement> & {
  /**
   * Allows the field to be empty, i.e. does not disable/hide the very top option.
   */
  allowEmpty?: boolean;

  /**
   * Expands the select control to display all of the options and allows users to choose multiple options at a time
   * Disabled by default
   */
  multiple?: boolean;

  /**
   * Adds a disabled initial option to the top of the list of options
   */
  placeholder?: string;
}

/**
 * A standard select/dropdown for easy reuse. It accepts all the same props as a native HTML select element.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESSelect: React.FC<React.PropsWithChildren<QESSelectProps>> = ({ allowEmpty, containerClassName, descriptiveText, errorText, hideLabel, isValid, label, multiple, placeholder, qesRef, required, testId, unrequiredValidation, wasFocusedExternally, validationTooltipContent, validationTooltipPosition, ...props }) => {
  const [hasBeenFocused, setHasBeenFocused] = React.useState(false);
  const [fieldState, setFieldState] = React.useState(FormFieldStates.None);

  React.useEffect(() => {
    if (!isValid && (required || unrequiredValidation) && hasBeenFocused) {
      setFieldState(FormFieldStates.Error);
    } else {
      setFieldState(FormFieldStates.None);
    }
  }, [isValid, hasBeenFocused, required, unrequiredValidation]);

  React.useEffect(() => {
    if (wasFocusedExternally) {
      setHasBeenFocused(wasFocusedExternally);
    }

  }, [wasFocusedExternally]);

  const subText = React.useMemo(() => {
    if (hasBeenFocused && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused]);

  return (
    <div className={`qes-select__container ${containerClassName || ""}`} key={testId} onBlur={() => setHasBeenFocused(true)}>
      <label
        htmlFor={props.id || testId}
        className={cx({
          "qes-select__label": true,
          "visually-hidden-label": hideLabel,
        })}
      >
        {label}
        {required ? <span className="text-danger"> *</span> : ""}
        {validationTooltipContent ? <TooltipWrapper containerClassName="ml-3" text={validationTooltipContent} position={validationTooltipPosition}><i className="fa fa-question-circle" /></TooltipWrapper> : ""}
      </label>
      <select
        {...props}
        id={props.id || testId}
        className={cx({
          "error": hasBeenFocused && fieldState === FormFieldStates.Error,
          "qes-select__select": true,
          [props.className]: !!props.className,
        })}
        data-testid={testId}
        required={required}
        multiple={!!multiple}
        ref={qesRef as React.RefObject<HTMLSelectElement>}
      >
        <option value="" disabled={!allowEmpty} hidden={!allowEmpty}>{placeholder || ""}</option>
        {props.children}
      </select>
      {subText && <small className="qes-select__descriptive-text">{subText}</small>}
    </div>
  );
};
